import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Loader2, Music2, Play, Pause, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const KIND = { installation: "Installation", immersion: "Immersion", reset: "Reset" };

function Row({ it, onData, playing, setPlaying }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const key = `${it.kind}:${it.ref_id}`;
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.post("/admin/music/upload", { key, file_id: up.data.file_id }); onData(data); toast.success("Track set");
    } catch { toast.error("Upload failed"); }
    setBusy(false);
  };
  const regen = async () => { const { data } = await api.post("/admin/music/generate", { key, replace: true }); onData(data); };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border px-4 py-3" data-testid={`music-${key}`}>
      <span className="text-[10px] uppercase tracking-widest text-primary w-20">{KIND[it.kind]}</span>
      <span className="flex-1 min-w-[8rem] text-sm">{it.title}
        <span className="block text-xs text-muted-foreground" data-testid={`music-status-${key}`}>
          {it.generating ? "Composing…" : it.url ? (it.source === "upload" ? "Your track" : "AI-made") : it.error ? "Failed — try again" : "No music yet"}
        </span>
      </span>
      {it.url && (
        <button onClick={() => setPlaying(playing === key ? null : key)} aria-label="Preview" className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center" data-testid={`music-preview-${key}`}>
          {playing === key ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
      )}
      <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`music-upload-${key}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" /> Suno track</>}
      </Button>
      <Button size="sm" variant="ghost" className="rounded-full" onClick={regen} disabled={it.generating} data-testid={`music-regen-${key}`}><RefreshCw className="w-3.5 h-3.5" /></Button>
    </div>
  );
}

export default function AdminMusic() {
  const [items, setItems] = useState(null);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const load = () => api.get("/admin/music").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!items?.some((i) => i.generating)) return;
    const t = setInterval(load, 8000); return () => clearInterval(t);
  }, [items]);
  useEffect(() => {
    audioRef.current?.pause();
    const it = items?.find((i) => `${i.kind}:${i.ref_id}` === playing);
    if (it?.url) { audioRef.current = new Audio(`${BACKEND_URL}${it.url}`); audioRef.current.play().catch(() => {}); }
    return () => audioRef.current?.pause();
  }, [playing]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items) return null;
  const onData = (d) => setItems(d.items);
  const generateAll = async () => { const { data } = await api.post("/admin/music/generate", {}); onData(data); toast.success("Composing music for everything missing"); };
  return (
    <div className="mt-14 pt-10 border-t border-border" data-testid="admin-music">
      <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Music</p>
      <h2 className="font-serif text-3xl sm:text-4xl mb-2 flex items-center gap-2"><Music2 className="w-6 h-6 text-primary" /> Matching music</h2>
      <p className="text-muted-foreground mb-5 max-w-2xl">Each installation, immersion and reset has its own soft music, sitting low under her voice. Upload a Suno track to replace any AI-made one. After changing an installation's music, approve it again in "Full-length & Sleep" to re-mix.</p>
      <Button className="rounded-full mb-5" onClick={generateAll} data-testid="music-generate-all">Compose all missing music</Button>
      <div className="space-y-2">{items.map((it) => <Row key={`${it.kind}:${it.ref_id}`} it={it} onData={onData} playing={playing} setPlaying={setPlaying} />)}</div>
    </div>
  );
}
